/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';

import { IDefaMediaGalleryData } from './defa-media-gallery.data';
import { IDefaMediaGalleryProps } from './defa-media-gallery.props.autogenerated';
import { MediaLocation } from '@msdyn365-commerce/retail-proxy';
import ReactDOM from 'react-dom';
import ZoomedImage from './components/zoomed-image';

export interface IDefaMediaGalleryViewProps extends IDefaMediaGalleryProps<IDefaMediaGalleryData> {
    componentRef: React.RefObject<HTMLDivElement>;
    baseClassName: string;
    ModuleProps: IModuleProps;
    MediaGalleryContainerProps: INodeProps;
    slideshow: JSX.Element | null;
    zoomedInImage: JSX.Element | null;
}

interface IDefaMediaGalleryState {
    images: MediaLocation[];
    zoomedImage?: MediaLocation | null;
    zoomedInImageIndex?: number;
    scrollOffset: number;
    loadedImages: Set<string>;
    failedImages: Set<string>;
}

/**
 *
 * DefaMediaGallery component
 * @extends {React.PureComponent<IDefaMediaGalleryProps<IDefaMediaGalleryData>>}
 */
class DefaMediaGallery extends React.PureComponent<IDefaMediaGalleryProps<IDefaMediaGalleryData>, IDefaMediaGalleryState> {
    private baseClassName: string = 'defa-media-gallery';

    private componentRef: React.RefObject<HTMLDivElement> = React.createRef();
    private zoomedInWrapperRef: React.RefObject<HTMLDivElement> = React.createRef();

    private imageParameters: string = 'w=550&h=550&f=webp&m=1';
    private thumbnailImageParameters: string = 'w=116&h=116&f=webp&m=1';

    constructor(props: IDefaMediaGalleryProps<IDefaMediaGalleryData>) {
        super(props);
        this.state = {
            images: props.data.mediaLocationsForSelectedVariant.result || [],
            zoomedImage: null,
            scrollOffset: 0,
            loadedImages: new Set<string>(),
            failedImages: new Set<string>()
        };
        this.onImageClick = this.onImageClick.bind(this);
        this.closeZoomedInImage = this.closeZoomedInImage.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        // Add right and left hotkey
        document.addEventListener('keydown', this.handleKeyDown);

        const images = await this.props.data.mediaLocationsForSelectedVariant;

        this.setState({ images });

        // Set fallback images for any that failed to load before component mounted
        if (this.componentRef.current) {
            const images = this.componentRef.current.getElementsByTagName('img');
            const loadedImages = new Set(this.state.loadedImages);
            const failedImages = new Set(this.state.failedImages);

            for (let i = 0; i < images.length; i++) {
                const img = images[i];
                if (img.complete) {
                    loadedImages.add(img.dataset.imageUri || '');

                    if (img.naturalWidth === 0) {
                        failedImages.add(img.dataset.imageUri || '');
                    }
                }
            }

            this.setState((prevState) => {
                const images = [ ...prevState.images ].filter((img) => !failedImages.has(img.Uri || ''));

                return {
                    ...prevState,
                    images,
                    loadedImages,
                    failedImages
                };
            });
        }
    }

    public componentWillUnmount(): void {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    private handleKeyDown = (event: KeyboardEvent): void => {
        const { zoomedImage, zoomedInImageIndex } = this.state;

        // Only active if zoomed in
        if (!zoomedImage) {
            return;
        }

        const { images } = this.state;

        if (event.key === 'ArrowRight') {
            if (zoomedInImageIndex !== undefined && zoomedInImageIndex < images.length - 1) {
                this.setState({
                    zoomedInImageIndex: zoomedInImageIndex + 1,
                    zoomedImage: images[zoomedInImageIndex + 1]
                });
            }
            event.preventDefault();
        } else if (event.key === 'ArrowLeft') {
            if (zoomedInImageIndex !== undefined && zoomedInImageIndex > 0) {
                this.setState({
                    zoomedInImageIndex: zoomedInImageIndex - 1,
                    zoomedImage: images[zoomedInImageIndex - 1]
                });
            }
            event.preventDefault();
        } else if (event.key === 'Escape') {
            this.closeZoomedInImage();
            event.preventDefault();
        }
    };

    public render(): JSX.Element | null {
        const viewProps: IDefaMediaGalleryViewProps = {
            ...this.props,
            componentRef: this.componentRef,
            ModuleProps: {
                moduleProps: this.props,
                className: this.baseClassName
            },
            MediaGalleryContainerProps: {
                className: `${this.baseClassName}__container`
            },
            baseClassName: this.baseClassName,
            slideshow: this.renderSlideshow(),
            zoomedInImage: this.renderZoomedInImage()
        };
        return this.props.renderView(viewProps);
    }

    private addImageParameters(baseUri: string, params: string): string {
        if (baseUri.indexOf('?') === -1) {
            return `${baseUri}?${params}`;
        }
        return `${baseUri}&${params}`;
    }

    private renderSlideshow(): JSX.Element | null {
        const useOmniChannelMedia = this.props.context.app.config?.OmniChannelMedia as boolean | undefined;
        const { images } = this.state;
        const fallbackImageSrc = this.getFallbackImageSrc();

        // First sort images by "IsDefault", which will always be the first image, then by "DisplayOrder"
        images.sort((a, b) => {
            if (a.IsDefault && !b.IsDefault) {
                return -1;
            }
            if (!a.IsDefault && b.IsDefault) {
                return 1;
            }
            return (a.DisplayOrder || 0) - (b.DisplayOrder || 0);
        });

        return (
            <div className={`${this.baseClassName}__slideshow`} style={{ '--scroll-offset': this.state.scrollOffset } as React.CSSProperties}>
                {images.map((image, index) => {
                    const imageUri = image.Uri
                        ? this.addImageParameters(image.Uri, index === 0 ? this.imageParameters : this.thumbnailImageParameters)
                        : undefined;
                    const isLoaded = image.Uri ? this.state.loadedImages.has(image.Uri) : false;
                    const isFailed = image.Uri ? this.state.failedImages.has(image.Uri) : false;
                    const imgSrc = imageUri && !isFailed ? imageUri : fallbackImageSrc;

                    return (
                        <div key={image.Uri || index} className={`${this.baseClassName}__slideshow__image-container`}>
                            <img
                                src={imgSrc}
                                title={image.AltText}
                                data-image-uri={image.Uri}
                                data-fallback={imgSrc === fallbackImageSrc ? 'true' : 'false'}
                                onLoad={() => {
                                    if (image.Uri) {
                                        const loadedImages = new Set(this.state.loadedImages);
                                        loadedImages.add(image.Uri);
                                        this.setState({ loadedImages });
                                    }
                                }}
                                onError={() => {
                                    if (image.Uri) {
                                        const images = [...this.state.images].filter((img) => img.Uri !== image.Uri);
                                        const loadedImages = new Set(this.state.loadedImages);
                                        const failedImages = new Set(this.state.failedImages);
                                        loadedImages.add(image.Uri);
                                        failedImages.add(image.Uri);
                                        this.setState({ images, loadedImages, failedImages });
                                    }
                                }}
                                onClick={() => this.onImageClick(index, image)}
                                style={!useOmniChannelMedia && index > 0 && (!isLoaded || isFailed) ? { visibility: 'hidden' } : undefined}
                            />
                        </div>
                    );
                })}
                {images.length > 5 && (
                    <div className={`${this.baseClassName}__slideshow__controls`}>
                        <button
                            disabled={this.state.scrollOffset === 0}
                            onClick={() => {
                                // Add 4 up to max 0
                                this.setState({ scrollOffset: Math.min(this.state.scrollOffset + 4, 0) });
                            }}
                        >
                            {this.props.resources.previousButtonText}
                        </button>
                        <button
                            disabled={Math.abs(this.state.scrollOffset) >= images.length - 5}
                            onClick={() => {
                                // Subtract 4 up to max images.length -5
                                const maxOffset = -(images.length - 5);
                                this.setState({ scrollOffset: Math.max(this.state.scrollOffset - 4, maxOffset) });
                            }}
                        >
                            {this.props.resources.nextButtonText}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    private renderZoomedInImage(): JSX.Element | null {
        if (!this.state.zoomedImage || typeof window === 'undefined') {
            return null;
        }

        const { resources } = this.props;
        const { images } = this.state;
        const { zoomedImage, zoomedInImageIndex } = this.state;

        const element = (
            <div className={`${this.baseClassName}__zoomed-in-image__wrapper`} ref={this.zoomedInWrapperRef}>
                <div className={`${this.baseClassName}__zoomed-in-image__topbar`}>
                    <div>
                        {(zoomedInImageIndex || 0) + 1} / {images.length}
                    </div>
                    <div className={`${this.baseClassName}__zoomed-in-image__actions`}>
                        <button
                            className={`${this.baseClassName}__zoomed-in-image__actions__fullscreen`}
                            title={resources.mediaGalleryFullscreenButtonTitle}
                            onClick={() => {
                                if (this.zoomedInWrapperRef.current) {
                                    if (document.fullscreenElement) {
                                        void document.exitFullscreen();
                                    } else {
                                        void this.zoomedInWrapperRef.current.requestFullscreen();
                                    }
                                }
                            }}
                        />
                        <button
                            className={`${this.baseClassName}__zoomed-in-image__actions__close`}
                            title={resources.mediaGalleryCloseButtonTitle}
                            onClick={() => {
                                this.closeZoomedInImage();
                            }}
                        />
                    </div>
                </div>
                <ZoomedImage
                    baseClassName={this.baseClassName}
                    closeZoomedInImage={this.closeZoomedInImage}
                    zoomedImage={zoomedImage}
                    goToPreviousButton={
                        <button
                            className={`${this.baseClassName}__zoomed-in-image__previous`}
                            disabled={images.length <= 1}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (zoomedInImageIndex !== undefined && zoomedInImageIndex > 0) {
                                    this.setState({
                                        zoomedInImageIndex: zoomedInImageIndex - 1,
                                        zoomedImage: images[zoomedInImageIndex - 1]
                                    });
                                } else if (zoomedInImageIndex === 0) {
                                    this.setState({
                                        zoomedInImageIndex: images.length - 1,
                                        zoomedImage: images[images.length - 1]
                                    });
                                }
                            }}
                            title={resources.mediaGalleryPreviousButtonTitle}
                        />
                    }
                    goToNextButton={
                        <button
                            className={`${this.baseClassName}__zoomed-in-image__next`}
                            disabled={images.length <= 1}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (zoomedInImageIndex !== undefined && zoomedInImageIndex < images.length - 1) {
                                    this.setState({
                                        zoomedInImageIndex: zoomedInImageIndex + 1,
                                        zoomedImage: images[zoomedInImageIndex + 1]
                                    });
                                } else if (zoomedInImageIndex === images.length - 1) {
                                    this.setState({
                                        zoomedInImageIndex: 0,
                                        zoomedImage: images[0]
                                    });
                                }
                            }}
                            title={resources.mediaGalleryNextButtonTitle}
                        />
                    }
                />
            </div>
        );

        return ReactDOM.createPortal(element, document.body);
    }

    private onImageClick(index: number, image: MediaLocation): void {
        this.setState({ zoomedInImageIndex: index, zoomedImage: image });
        document.body.classList.add('overflow-hidden');
    }

    private closeZoomedInImage = (): void => {
        this.setState({ zoomedImage: null });
        document.body.classList.remove('overflow-hidden');
    };

    /**
     * Converts a given string representing a file path with an extension to multiple formats.
     * @param inputString The input string representing the file path with extension.
     * @returns A string containing comma-separated paths for each supported format.
     */
    private convertToMultipleExtensions = (inputString: string): string => {
        const formats = ['jpg', 'png', 'webp'];

        const baseParts = inputString.split('.', 1);
        if (baseParts.length > 0) {
            const [basePath] = baseParts;

            return formats.map(ext => `${basePath}.${ext}`).join(',');
        }
        return inputString;
    };

    private getFallbackImageSrc = (): string | undefined => {
        const placeholderImageName = this.props.context.request.app.config?.placeholderImageName as string | undefined;
        if (placeholderImageName) {
            const baseImageUrl = this.props.context.request.apiSettings.baseImageUrl;
            return `${baseImageUrl}&fallback=${this.convertToMultipleExtensions(placeholderImageName)}`;
        }
        return;
    };
}

export default DefaMediaGallery;
